import { onLinkClick } from 'render/click_handlers';
import Link from 'models/link';
import Topic from '../../../cli/shared/topic';
import Path from 'models/path';
import { projectPathPrefix, hashUrls } from 'helpers/getters';
import { measureVerticalOverflow } from 'render/helpers';

const rtlPattern = /[\p{Script=Hebrew}\p{Script=Arabic}\p{Script=Syriac}\p{Script=Thaana}\p{Script=Nko}\p{Script=Samaritan}\p{Script=Mandaic}\p{Script=Adlam}\p{Script=Hanifi_Rohingya}\p{Script=Phoenician}\p{Script=Imperial_Aramaic}]/u;
const ltrPattern = /[\p{Script=Latin}\p{Script=Greek}\p{Script=Cyrillic}\p{Script=Devanagari}\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}\p{Number}]/u;

function isRtlText(text) {
  for (const character of Array.from((text || '').trim())) {
    if (rtlPattern.test(character)) return true;
    if (ltrPattern.test(character)) return false;
  }
  return false;
}

function renderLinkBase(token, renderContext, renderTokenElements) {
  let linkElement = document.createElement('a');
  linkElement.classList.add('canopy-selectable-link');
  linkElement.dataset.enclosingTopic = token.enclosingTopic;
  linkElement.dataset.enclosingSubtopic = token.enclosingSubtopic;
  linkElement.dataset.text = token.text;

  linkElement.dir = 'auto'; // necessary to get link icons with RTL link text on correct side and not breaking border

  let linkContainer = document.createElement('SPAN');
  linkContainer.classList.add('canopy-link-container');
  linkContainer.dir = 'auto';

  let contentContainer = document.createElement('SPAN');
  contentContainer.classList.add('canopy-link-content-container');
  contentContainer.dir = 'auto';

  linkContainer.appendChild(contentContainer);
  linkElement.appendChild(linkContainer);

  const terminalGap = document.createElement('span');
  terminalGap.classList.add('canopy-link-terminal-gap');
  linkElement.appendChild(terminalGap);

  // Append subtoken elements
  token.tokens.forEach(subtoken => {
    let subtokenElements = renderTokenElements(subtoken, renderContext);
    subtokenElements.forEach(subtokenElement => contentContainer.appendChild(subtokenElement));
  });
  const linkDirectionText = (contentContainer.textContent || '').trim();
  linkElement.dataset.linkDir = isRtlText(linkDirectionText) ? 'rtl' : 'ltr';

  // Prevent dragging for easier text selection in tables
  linkElement.addEventListener('dragstart', (e) => {
    e.preventDefault();
  });

  // Add click event handler
  let link = new Link(linkElement);
  let callback = onLinkClick(link);
  linkElement._CanopyClickHandler = callback;
  linkElement.addEventListener('click', callback);

  const preDisplayLinkLayout = () => {
    const singletonTableOrMenuButton = !!linkElement.closest('.canopy-table-link-cell, .canopy-menu-link-cell');
    const inlineLayoutEnabled = !singletonTableOrMenuButton;
    let spaceAbove = 0;
    let spaceBelow = 0;

    // Read phase: collect measurements before mutating styles/classes.
    let lineHeight = 0;
    let height = 0;
    if (inlineLayoutEnabled) {
      [spaceAbove, spaceBelow] = measureVerticalOverflow(contentContainer);
      const contentStyle = window.getComputedStyle(contentContainer);
      lineHeight = parseFloat(contentStyle.lineHeight);
      height = contentContainer.getBoundingClientRect().height;
    }

    let direction = null;
    let cursor = linkElement;
    while (cursor && cursor.tagName !== 'P') {
      const dirAttr = cursor.getAttribute?.('dir');
      if (dirAttr && dirAttr !== 'auto') {
        direction = dirAttr;
        break;
      }
      cursor = cursor.parentNode;
    }
    if (!direction) {
      direction = window.getComputedStyle(contentContainer).direction;
    }

    // Write phase: apply style/class/attribute changes after reads.
    if (inlineLayoutEnabled) {
      linkElement.dataset.extraSpace = JSON.stringify([spaceAbove, spaceBelow]);
      if (spaceAbove) contentContainer.style.paddingTop = `${spaceAbove}px`;
      if (spaceBelow) contentContainer.style.paddingBottom = `${spaceBelow}px`;
    }

    // Multiline means the link's own content spans more than one visual line,
    // whether from wrapping or explicit line breaks inside the link.
    if (inlineLayoutEnabled && lineHeight > 0 && height >= lineHeight * 1.7) {
      linkElement.dataset.height = height;
      linkElement.dataset.lineHeight = lineHeight;
      linkElement.classList.add('canopy-multiline-link');
    }

    const parent = linkElement.parentElement;
    const isBlockParent = parent?.tagName === 'P' || parent?.tagName === 'BLOCKQUOTE';
    const isFirst = linkElement === parent?.firstElementChild;
    const isLast = linkElement === parent?.lastElementChild;
    const prevIsBreak = linkElement.previousElementSibling?.classList.contains('canopy-linebreak-span');
    const nextIsBreak = linkElement.nextElementSibling?.classList.contains('canopy-linebreak-span');
    // Full-line means the link is structurally alone between paragraph/block
    // quote boundaries or explicit linebreak spans.
    const isFullLine = isBlockParent && ((isFirst || prevIsBreak) && (isLast || nextIsBreak));
    linkElement.classList.toggle('canopy-full-line-link', isFullLine);

    linkElement.dir = direction;
    linkContainer.dir = direction;
    contentContainer.dir = direction;
  };
  renderContext.preDisplayCallbacks.push(preDisplayLinkLayout);

  return linkElement;
}

function renderLocalLink(token, renderContext, renderTokenElements) {
  let linkElement = renderLinkBase(token, renderContext, renderTokenElements);

  // Set specific attributes for local links
  linkElement.classList.add('canopy-local-link');
  linkElement.dataset.type = 'local';
  linkElement.dataset.targetTopic = token.targetTopic;
  linkElement.targetTopic = token.targetTopic; // Helpful for debugger
  linkElement.dataset.targetSubtopic = token.targetSubtopic;
  linkElement.targetSubtopic = token.targetSubtopic; // Helpful for debugger

  let targetTopic = new Topic(token.targetTopic);
  let targetSubtopic = new Topic(token.targetSubtopic);
  linkElement.href = `${projectPathPrefix ? '/' + projectPathPrefix : ''}${hashUrls ? '/#' : ''}/${targetTopic.url}#${targetSubtopic.url}`;

  return [linkElement];
}

function renderGlobalLink(token, renderContext, renderTokenElements) {
  let linkElement = renderLinkBase(token, renderContext, renderTokenElements);

  // Set specific attributes for global links
  linkElement.classList.add('canopy-global-link');
  linkElement.dataset.type = 'global';
  linkElement.dataset.literalPathString = token.pathString;
  linkElement.path = token.path; // Helpful for debugger
  linkElement.href = Path.for(token.pathString).productionPathString;

  let link = new Link(linkElement);
  
  const preDisplayGlobalLinkCycleIcon = () => {
    if (!link.element || link.type === 'disabled') return;
    if ((link.cycle || link.isSelfReference) && !link.isSelfTerminalReference) {
      const targetPath = link.inlinePath?.reduce();
      const fulcrumPath = targetPath ? link.enclosingPath.initialOverlap(targetPath) : null;
      const fulcrumParagraph = fulcrumPath?.paragraph;
      if (fulcrumParagraph?.paragraphElement) {
        link.element.dataset.fulcrumDir = isRtlText(fulcrumParagraph.paragraphElement.textContent) ? 'rtl' : 'ltr';
      }

      if (containsIconOrEmoji(link.text) || isSingleCharacterLink(link.text)) { // user is taking responsibility for arrow
        return;
      }

      const cycleIcon = document.createElement('span');

      if (link.isForwardCycle) {
        cycleIcon.classList.add('canopy-forward-cycle-icon');
        cycleIcon.innerText = '↪';
      } else if (link.isDownCycle) {
        cycleIcon.classList.add('canopy-down-cycle-icon');
        cycleIcon.classList.add(`canopy-down-${link.downCycleDirection || 'ahead'}-cycle-icon`);
        cycleIcon.innerText = '↪';
      } else if (link.isUpCycle || link.isSelfReference) {
        cycleIcon.classList.add('canopy-up-cycle-icon');
        cycleIcon.innerText = '↩';
      } else if (link.isBackCycle) {
        cycleIcon.classList.add('canopy-back-cycle-icon');
        cycleIcon.innerText = '↩';
      }

      const linkContainer = linkElement.querySelector('.canopy-link-container');
      const iconGap = document.createElement('span');
      iconGap.classList.add('canopy-link-icon-gap');
      linkContainer.appendChild(iconGap);
      linkContainer.appendChild(cycleIcon);
    }
  };
  renderContext.preDisplayCallbacks.push(preDisplayGlobalLinkCycleIcon);

  return [linkElement];
}

function renderDisabledLink(token, renderContext, renderTokenElements) {
  // Build the same link the user would get (local vs global), then disable.
  let linkElement;
  if (token.targetTopic && token.targetSubtopic) {
    [linkElement] = renderLocalLink(token, renderContext, renderTokenElements);
  } else if (token.pathString) {
    [linkElement] = renderGlobalLink(token, renderContext, renderTokenElements);
  } else {
    // Fallback: plain base if neither local nor global fields are present
    linkElement = renderLinkBase(token, renderContext, renderTokenElements);
  }

  // Disable interactivity and navigation, keep content and layout identical.
  linkElement.classList.remove('canopy-selectable-link');
  linkElement.classList.add('canopy-disabled-link');
  linkElement.dataset.type = 'disabled';
  linkElement.removeAttribute('href');
  linkElement.setAttribute('aria-disabled', 'true');
  linkElement.setAttribute('tabindex', '-1');

  if (linkElement._CanopyClickHandler) {
    linkElement.removeEventListener('click', linkElement._CanopyClickHandler);
    delete linkElement._CanopyClickHandler;
  }
  // Hard-stop any stray clicks (e.g., nested elements)
  linkElement.addEventListener('click', e => e.preventDefault(), { capture: true });

  return [linkElement];
}

function renderFragmentReference(token, renderContext, renderTokenElements) {
  let linkElement = renderLinkBase(token, renderContext, renderTokenElements);

  // Add fragment reference-specific classes and attributes
  linkElement.classList.add('canopy-local-link');
  linkElement.dataset.type = 'local';
  linkElement.dataset.targetTopic = token.targetTopic;
  linkElement.targetTopic = token.targetTopic; // Helpful for debugging
  linkElement.dataset.targetSubtopic = token.targetSubtopic;
  linkElement.targetSubtopic = token.targetSubtopic; // Helpful for debugging

  // Construct the href attribute based on projectPathPrefix and hashUrls
  let targetTopic = new Topic(token.targetTopic);
  let targetSubtopic = new Topic(token.targetSubtopic);
  linkElement.href = `${projectPathPrefix ? '/' + projectPathPrefix : ''}${hashUrls ? '/#' : ''}/${targetTopic.url}#${targetSubtopic.url}`;

  return [linkElement];
}

function renderExternalLink(token, renderContext, renderTokenElements) {
  let linkElement = renderLinkBase(token, renderContext, renderTokenElements);

  // Add external link-specific classes and attributes
  linkElement.classList.add('canopy-external-link');
  linkElement.classList.toggle('canopy-external-icon-only-link', !!token.iconOnly);
  linkElement.dataset.type = 'external';
  linkElement.setAttribute('href', token.url); // URL validation assumed to be done at the matcher/token stage
  linkElement.setAttribute('target', '_blank');
  linkElement.dataset.targetUrl = token.url;
  if (token.iconOnly) linkElement.setAttribute('aria-label', token.url);

  // Add external link icon
  let cycleIcon = document.createElement('span');
  cycleIcon.classList.add('canopy-external-link-icon');
  const linkContainer = linkElement.querySelector('.canopy-link-container');
  if (!token.iconOnly) {
    const iconGap = document.createElement('span');
    iconGap.classList.add('canopy-link-icon-gap');
    linkContainer.appendChild(iconGap);
  }
  linkContainer.appendChild(cycleIcon);

  // Add a class if the link contains an image
  const preDisplayExternalLinkedImageClass = () => {
    if (linkElement.querySelector('img')) {
      linkElement.classList.add('canopy-linked-image');
    }
  };
  renderContext.preDisplayCallbacks.push(preDisplayExternalLinkedImageClass);

  return [linkElement];
}

function containsIconOrEmoji(str) {
  if (!str) return false;
  const plainText = str.replace(/<[^>]*>/g, ''); // avoid treating HTML markup as symbols
  const arrowPattern = /[\u2190-\u21FF\u2794-\u27BF\u27F0-\u27FF\u2900-\u297F\u2B00-\u2B4F]/u;
  const emojiPattern = /(?:\p{Emoji_Presentation}|\p{Extended_Pictographic}|\p{Emoji}\uFE0F)/u;
  return arrowPattern.test(plainText) || emojiPattern.test(plainText);
}

function isSingleCharacterLink(str) {
  if (!str) return false;
  const plainText = str.replace(/<[^>]*>/g, '').trim();
  if (!plainText) return false;
  return Array.from(plainText).length === 1;
}

export {
  renderLocalLink,
  renderGlobalLink,
  renderDisabledLink,
  renderFragmentReference,
  renderExternalLink,
};
